/** SUNTRONIX ONE — Exception & Escalation Engine v2 (admin).
 *
 *  Consumes the new `/api/exception-engine` endpoints — full record
 *  schema with owner, blocking, next_action, evidence, timeline, and
 *  server-authoritative status.  Saved views + Resolve/Escalate/Close
 *  drawers live inside this single page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, RefreshCcw, ShieldAlert, Send, CheckCircle2, XCircle,
  Ban, Timer, Zap, ChevronRight, Flame, User, Clock, ShieldCheck, Wand2,
} from "lucide-react";
import InwardReconciliationWizard from "@/components/app/InwardReconciliationWizard";

const SEV_TONE = {
  P0: "text-red-800 bg-red-100 border-red-300 font-bold",
  P1: "text-red-700 bg-red-50 border-red-200",
  P2: "text-amber-700 bg-amber-50 border-amber-200",
  P3: "text-slate-700 bg-slate-100 border-slate-200",
  P4: "text-slate-500 bg-slate-50 border-slate-200",
};
const BLOCK_TONE = {
  hard:         "text-red-800 bg-red-50 border-red-300",
  soft:         "text-amber-800 bg-amber-50 border-amber-300",
  non_blocking: "text-slate-700 bg-slate-100 border-slate-200",
  info:         "text-slate-500 bg-slate-50 border-slate-200",
};

const SAVED_VIEWS = [
  { id: "needs_action",    label: "Needs Action",       query: { status: "active" } },
  { id: "blocking",        label: "Blocking Operations", query: { status: "active", blocking: "hard" } },
  { id: "p0p1",            label: "P0 / P1",            query: { status: "active", severity: "P1" } },
  { id: "overdue",         label: "Overdue",            query: { status: "overdue" } },
  { id: "unassigned",      label: "Unassigned",         query: { status: "active", owner_email: "" } },
  { id: "recent_resolved", label: "Recently Resolved",  query: { status: "resolved" } },
];

function timeFmt(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

function dueFmt(iso, overdueHours) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (overdueHours > 0) {
    return <span className="text-red-700 font-semibold">Overdue {overdueHours.toFixed(1)}h</span>;
  }
  const mins = Math.floor((d.getTime() - Date.now()) / 60000);
  if (mins < 60) return <span className="text-amber-600">in {mins}m</span>;
  if (mins < 24*60) return <span className="text-slate-600">in {Math.floor(mins/60)}h</span>;
  return <span className="text-slate-500">in {Math.floor(mins/60/24)}d</span>;
}

export default function AdminExceptions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuntronix = user && !user.master_company_id;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("needs_action");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null);  // "resolve" | "escalate" | "close"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [reconcileExc, setReconcileExc] = useState(null);

  const currentQuery = useMemo(() => {
    const v = SAVED_VIEWS.find((v) => v.id === view);
    return v?.query || { status: "active" };
  }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sm] = await Promise.all([
        api.get("/exception-engine", { params: currentQuery }),
        api.get("/exception-engine/summary"),
      ]);
      let items = list.data.items || [];
      if (view === "unassigned") {
        items = items.filter((r) => !r.current_owner_email);
      }
      setRows(items);
      setSummary(sm.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load exceptions");
    } finally { setLoading(false); }
  }, [currentQuery, view]);

  useEffect(() => { if (isSuntronix) load(); }, [isSuntronix, load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      `${r.title} ${r.code} ${r.entity_code} ${r.category} ${r.current_owner_email || ""}`
        .toLowerCase().includes(query));
  }, [rows, q]);

  const openAction = (row, kind) => {
    setSelected(row);
    setAction(kind);
    setForm({});
  };

  const submit = async () => {
    if (!selected || !action) return;
    setBusy(true);
    try {
      if (action === "resolve") {
        if (!form.evidence_note?.trim()) {
          toast.error("Add a resolution evidence note.");
          setBusy(false); return;
        }
        await api.post(`/exception-engine/${selected.id}/resolve`, {
          evidence: { note: form.evidence_note.trim() },
          reason: form.reason || null,
          root_cause: form.root_cause || null,
          corrective_action: form.corrective_action || null,
        });
        toast.success("Resolved with evidence");
      } else if (action === "escalate") {
        if (!form.reason?.trim()) {
          toast.error("Reason is required.");
          setBusy(false); return;
        }
        await api.post(`/exception-engine/${selected.id}/escalate`, {
          reason: form.reason.trim(),
          to_email: form.to_email || null,
        });
        toast.success("Escalated one level");
      } else if (action === "close") {
        if (!form.reason?.trim()) {
          toast.error("Reason is required.");
          setBusy(false); return;
        }
        await api.post(`/exception-engine/${selected.id}/close-not-issue`, {
          reason: form.reason.trim(),
          evidence: form.evidence_note ? { note: form.evidence_note } : {},
        });
        toast.success("Closed as not-an-issue");
      }
      setSelected(null); setAction(null); setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (!isSuntronix) {
    return (
      <div className="max-w-lg mx-auto mt-16 st-panel p-6 text-center"
            data-testid="exc-forbidden">
        <ShieldAlert className="h-8 w-8 mx-auto text-slate-400 mb-2" />
        <div className="font-heading text-lg mb-1">Suntronix internal only</div>
        <div className="text-sm text-slate-500">
          The Exception & Escalation Engine is restricted to Suntronix operators.
        </div>
      </div>
    );
  }

  const s = summary || {};

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="exceptions-v2-page">
      <PageHeader
        eyebrow="Operations · Reliability"
        title="Exceptions & Escalations"
        context="Operational issues requiring action across technology lifecycle workflows. Every row has an owner, a next action and a deadline."
        actions={
          <Button variant="outline" onClick={load} disabled={loading}
            data-testid="exc-refresh">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "open",     label: "Open",       value: s.open_total || 0,           tone: "text-red-700",    icon: AlertTriangle },
          { key: "P1",       label: "P1",         value: s.by_severity?.P1 || 0,     tone: "text-red-700",    icon: Flame },
          { key: "hard",     label: "Hard block", value: s.by_blocking?.hard || 0,   tone: "text-red-800",    icon: Ban },
          { key: "overdue",  label: "Overdue",    value: s.overdue || 0,             tone: "text-red-700",    icon: Timer },
          { key: "soft",     label: "Soft block", value: s.by_blocking?.soft || 0,   tone: "text-amber-700",  icon: Zap },
        ].map((k) => (
          <div key={k.key} className="st-panel p-3"
            data-testid={`exc-kpi-${k.key}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <k.icon className="h-3 w-3" /> {k.label}
            </div>
            <div className={`text-2xl font-heading font-semibold mt-1 tabular-nums ${k.tone}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Saved views + search */}
      <div className="flex flex-wrap items-center gap-2">
        {SAVED_VIEWS.map((v) => (
          <button key={v.id}
             onClick={() => setView(v.id)}
             data-testid={`exc-view-${v.id}`}
             className={`text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded border
              ${view === v.id ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {v.label}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter title, code, entity, owner…"
          className="max-w-xs h-9 text-[13px] ml-auto"
          data-testid="exc-search" />
      </div>

      {/* Table */}
      <Section title="Exceptions" testId="exc-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Blocking</th>
                <th className="text-left px-3 py-2">Exception</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-left px-3 py-2">Age</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-400 text-[12px] py-8">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-emerald-700 text-[12px] py-8"
                          data-testid="exc-empty">
                  Nothing matches this view — everything is inside SLA. ✨
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id}
                      className={`border-t border-slate-100 hover:bg-slate-50
                        ${r.blocking === "hard" ? "border-l-4 border-l-red-600" : ""}`}
                      data-testid={`exc-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${SEV_TONE[r.severity] || SEV_TONE.P3}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${BLOCK_TONE[r.blocking] || BLOCK_TONE.info}`}>
                      {r.blocking.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-[13px] text-slate-900">{r.title}</div>
                    <div className="text-[10px] text-slate-500">{r.category} · {r.code}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-[12px]">{r.entity_kind}</div>
                    <div className="font-mono text-[10px] text-slate-500">{r.entity_code || r.entity_id.slice(0,8)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-[12px] text-slate-700">
                      {r.current_owner_email || <span className="text-red-600">Unassigned</span>}
                    </div>
                    {r.owner_team && (
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{r.owner_team}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[12px]">
                    {dueFmt(r.due_at, r.overdue_hours)}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {timeFmt(r.detected_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {r.rule_id === "EXC-INWARD-UNEXPECTED-SERIAL" && (
                        <Button size="sm" variant="outline"
                          onClick={() => setReconcileExc(r)}
                          data-testid={`exc-reconcile-${r.id}`}
                          className="border-amber-300 text-amber-800 hover:bg-amber-50">
                          <Wand2 className="h-3 w-3 mr-1" /> Reconcile
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                        onClick={() => openAction(r, "resolve")}
                        data-testid={`exc-resolve-${r.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => openAction(r, "escalate")}
                        data-testid={`exc-escalate-${r.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Escalate
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => openAction(r, "close")}
                        data-testid={`exc-close-${r.id}`}>
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                    {r.next_action && (
                      <div className="text-[10px] text-slate-500 mt-1 max-w-[240px] line-clamp-2">
                        {r.next_action}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Inward reconciliation wizard */}
      <InwardReconciliationWizard
        exception={reconcileExc}
        open={!!reconcileExc}
        onOpenChange={(v) => { if (!v) setReconcileExc(null); }}
        onResolved={() => load()}
      />

      {/* Action dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setAction(null); setForm({}); } }}>
        <DialogContent data-testid="exc-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action === "resolve" && "Resolve exception"}
              {action === "escalate" && "Escalate exception"}
              {action === "close" && "Close as not-an-issue"}
            </DialogTitle>
            <div className="text-[12px] text-slate-500">
              {selected?.title} · {selected?.entity_code}
            </div>
          </DialogHeader>

          {selected?.next_action && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Required next action
              </div>
              {selected.next_action}
            </div>
          )}
          {selected?.evidence_required && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">
                Evidence required
              </div>
              {selected.evidence_required}
            </div>
          )}

          {action === "resolve" && (
            <div className="space-y-3">
              <FormField label="Evidence note (required)">
                <Textarea rows={2}
                  value={form.evidence_note || ""}
                  onChange={(e) => setForm({ ...form, evidence_note: e.target.value })}
                  placeholder="Describe how you verified this is fixed…"
                  data-testid="exc-form-evidence" />
              </FormField>
              <FormField label="Root cause (optional)">
                <Input value={form.root_cause || ""}
                  onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                  data-testid="exc-form-root-cause"
                  placeholder="e.g. Integration mapping, customer data, courier…" />
              </FormField>
              <FormField label="Corrective action (optional)">
                <Input value={form.corrective_action || ""}
                  onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
                  data-testid="exc-form-corrective" />
              </FormField>
            </div>
          )}

          {action === "escalate" && (
            <div className="space-y-3">
              <FormField label="Reason (required)">
                <Textarea rows={2}
                  value={form.reason || ""}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="exc-form-reason" />
              </FormField>
              <FormField label="Escalate to (optional)">
                <Input value={form.to_email || ""}
                  onChange={(e) => setForm({ ...form, to_email: e.target.value })}
                  placeholder="ops-lead@suntronix.net"
                  data-testid="exc-form-to-email" />
              </FormField>
            </div>
          )}

          {action === "close" && (
            <div className="space-y-3">
              <FormField label="Reason (required)">
                <Textarea rows={2}
                  value={form.reason || ""}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Why is this not actually an issue?"
                  data-testid="exc-form-reason" />
              </FormField>
              <FormField label="Evidence (optional)">
                <Input value={form.evidence_note || ""}
                  onChange={(e) => setForm({ ...form, evidence_note: e.target.value })}
                  data-testid="exc-form-evidence" />
              </FormField>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setSelected(null); setAction(null); }}
              disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy}
              data-testid="exc-form-submit">
              {action === "resolve" && <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm resolve</>}
              {action === "escalate" && <><Send className="h-3.5 w-3.5 mr-1" /> Escalate</>}
              {action === "close" && <><XCircle className="h-3.5 w-3.5 mr-1" /> Close</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
